import React, { useState, useRef, useEffect } from "react";
import styles from "./addsingle.module.scss";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../firebase/firebase";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { handlePostRequest } from "../../../services/PostTemplate";

function AddSingleDialog({onclose}) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [showCamera, setShowCamera] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [creating, setCreating] = useState(false); // <--- Added for loading state
    const dispatch = useDispatch();

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);

    // Cleanup preview URL when dialog closes or file changes
    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
            setPreviewUrl(URL.createObjectURL(e.target.files[0]));
            setImageUrl(null); // reset preview
        }
    };

    const openCamera = async () => {
        setShowCamera(true);
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
            setStream(mediaStream);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    videoRef.current.play();
                }
            }, 100);
        } catch (err) {
            toast.error("Unable to access camera.");
            setShowCamera(false);
        }
    };

    const closeCamera = () => {
        setShowCamera(false);
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (video && canvas) {
            const ctx = canvas.getContext("2d");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0);
            canvas.toBlob((blob) => {
                const url = URL.createObjectURL(blob);
                setPreviewUrl(url); // Show preview in main dialog
                setFile(new File([blob], `captured-${Date.now()}.png`, { type: "image/png" }));
                setImageUrl(null);
                closeCamera();
            }, "image/png");
        }
    };

    // Upload to Firebase
    const handleUpload = async () => {
        if (!file) {
            toast.error("Please select or capture an image first!");
            return;
        }
        setUploading(true);
        try {
            const storageRef = ref(storage, `packages/${Date.now()}-${file.name}`);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            setImageUrl(url);
            toast.success("Image uploaded successfully!");
        } catch (err) {
            console.error(err);
            toast.error("Image upload failed!");
        } finally {
            setUploading(false);
        }
    };

    // Create package API
    const handleCreatePackage = async () => {
        if (!imageUrl) {
            toast.error("Please upload an image first!");
            return;
        }
        setCreating(true); // <--- Set loading state
        try {
            const data = {
                imageUrl: imageUrl,
            };
            const res = await dispatch(handlePostRequest(data, "/ocrpackages/single", true, true));
            if (res !== "error") {
                toast.success("Package created successfully!");
                setFile(null);
                setImageUrl(null);
                setPreviewUrl(null);
                onclose();
            }
            else{
                toast.error("Error creating package");
            }
        } catch (err) {
            console.error(err);
            toast.error("Error creating package");
        } finally {
            setCreating(false); // <--- Reset loading state
        }
    };

    return (
        <div className={styles.container}>
            <h2>Add Single Package</h2>

            {/* File input for selecting an image */}
            <input type="file" accept="image/*" onChange={handleFileChange} />

            <button type="button" className={styles.cameraBtn} onClick={openCamera}>
                Take Photo
            </button>

            {/* Show preview if available (captured or uploaded) */}
            {previewUrl && !imageUrl && (
                <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                        marginTop: "10px",
                        maxWidth: "200px",
                        borderRadius: "8px",
                        border: "2px solid #e0e0e0",
                    }}
                />
            )}

            {/* Show uploaded image */}
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt="Uploaded"
                    style={{
                        marginTop: "10px",
                        maxWidth: "200px",
                        borderRadius: "8px",
                        border: "2px solid #e0e0e0",
                    }}
                />
            )}

            {/* Upload button */}
            <button onClick={handleUpload} disabled={!file || uploading || imageUrl} className={styles.uploadBtn}>
                {uploading ? "Uploading..." : "Upload Image"}
            </button>

            {/* Create Package button with loading */}
            <button onClick={handleCreatePackage} disabled={!imageUrl || creating} className={styles.createBtn}>
                {creating ? "Creating..." : "Create Package"}
            </button>

            {/* Camera Modal */}
            {showCamera && (
                <div className={styles.cameraModal}>
                    <video ref={videoRef} autoPlay playsInline className={styles.videoPreview} style={{ maxWidth: "320px", borderRadius: "1rem" }} />
                    <div className={styles.cameraActions}>
                        <button onClick={capturePhoto} className={styles.captureBtn}>
                            Capture Photo
                        </button>
                        <button onClick={closeCamera} className={styles.closeBtn}>
                            Close
                        </button>
                    </div>
                    <canvas ref={canvasRef} style={{ display: "none" }} />
                </div>
            )}
        </div>
    );
}

export default AddSingleDialog;